import {
  Avatar,
  Button,
  Container,
  Divider,
  Paper,
  Text,
} from "@flpstudio/design-system";
import {
  Link,
  type LoaderFunctionArgs,
  type MetaFunction,
  useLoaderData,
} from "react-router";

import { httpClient } from "@/api/client";
import type { DocumentAuthorDto } from "@/api/openapi";
import { BookingWidget } from "@/components/organisms/Booking/BookingWidget";
import { CommentForm } from "@/components/organisms/Comment/CommentForm";
import { CommentList } from "@/components/organisms/Comment/CommentList";
import { ComponentError } from "@/components/organisms/Error/ComponentError";
import { SignUpBanner } from "@/components/organisms/SignUpBanner";
import { useAuth } from "@/hooks/use-auth";
import {
  useExpertDocuments,
  useExpertSoftwareSelection,
} from "@/hooks/use-expert";
import { useSignUpSearchParams } from "@/hooks/use-page-search-params";
import { BestPracticeDocumentViewer } from "@/screens/documentation/BestPracticeDocumentViewer";
import { SoftwareDocumentViewer } from "@/screens/documentation/SoftwareDocumentViewer";
import { ViewerLayout } from "@/screens/documentation/ViewerLayout";
import {
  Amplitude,
  CommentInteractionsEvent,
  DocumentCommentEvent,
  createDocumentEventProperties,
} from "@/third-party/amplitude";
import { faChevronLeft } from "@awesome.me/kit-af809b8b43/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { BestPracticeDocument, SDocument, SoftwareDocument } from "types";
import { paths } from "~/paths";
import { wrapAxiosError } from "~/utils/serverUtils";

export async function loader({ params, request }: LoaderFunctionArgs) {
  const cookieString = request.headers.get("cookie") || "";

  try {
    const documentData = await httpClient.paths[
      "/v1/document/publishes/{identifier}"
    ].get(
      {
        identifier: params.urlSlug ?? "",
      },
      null,
      {
        headers: {
          cookie: cookieString,
        },
      },
    );
    return { documentData: documentData.data };
  } catch (error) {
    throw wrapAxiosError(error);
  }
}

export default function DocumentViewer() {
  const { documentData } = useLoaderData<typeof loader>();
  const { user } = useAuth();
  const { openSignUpModal } = useSignUpSearchParams();

  const { data: expertDocuments } = useExpertDocuments({
    identifier: documentData?.author?.id,
  });
  const { data: expertSoftwares } = useExpertSoftwareSelection({
    identifier: documentData?.author?.id,
  });

  const handleDocumentCommentEventSend = (values: {
    action: string;
    commentCount?: number;
  }) => {
    Amplitude.track(DocumentCommentEvent.name, {
      action: values.action,
      comments_number: values.commentCount,
      ...createDocumentEventProperties(
        documentData as unknown as SDocument,
        expertDocuments?.length,
        expertSoftwares?.map((software) => software.name),
      ),
    });
  };

  const handleCommentInteractionsEventSend = (values: {
    action: string;
    commentCount?: number;
    userType?: string;
    singleCommentReactionCount?: number;
  }) => {
    Amplitude.track(CommentInteractionsEvent.name, {
      action: values.action,
      comments_number: values.commentCount,
      user_type: values.userType,
      comment_reactions_number: values.singleCommentReactionCount,
      ...createDocumentEventProperties(
        documentData as unknown as SDocument,
        expertDocuments?.length,
        expertSoftwares?.map((software) => software.name),
      ),
    });
  };

  return (
    <Container component="main" className="flex flex-col gap-4">
      <header className="flex flex-col flex-nowrap gap-2 lg:flex-row lg:items-center">
        {user && (
          <Button
            component={Link}
            variant="outline"
            aria-label="Go back to all documentations"
            className="hidden size-6 border-none p-0 lg:inline"
            to={paths.documentation.root}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>
        )}
        {!user && (
          <SignUpBanner
            trackingLocation="Document Page"
            className="-mx-6 -mt-6 rounded-none lg:hidden"
          />
        )}
        <span className="font-semibold text-2xl/normal">
          {documentData.docType === "SOFTWARE_CHECKLIST"
            ? documentData.software?.category?.prompt
            : documentData.topic?.title}
        </span>
      </header>
      <ViewerLayout
        documentNode={
          documentData.docType === "SOFTWARE_CHECKLIST" ? (
            <SoftwareDocumentViewer
              document={documentData as unknown as SoftwareDocument}
            />
          ) : (
            <BestPracticeDocumentViewer
              document={documentData as unknown as BestPracticeDocument}
            />
          )
        }
        bookingNode={
          user ? (
            <Paper
              component="aside"
              className="border border-[--mantine-color-gray-3] border-solid empty:hidden lg:border-none"
            >
              <BookingWidget
                expertList={
                  [
                    documentData.author,
                    ...(!(documentData.docType === "SOFTWARE_CHECKLIST")
                      ? documentData.relatedTopicDocuments?.map(
                          (document) => document.author,
                        ) || []
                      : []),
                  ] as DocumentAuthorDto[]
                }
              />
            </Paper>
          ) : null
        }
        commentNode={
          <Paper component="aside" className="-mx-6 lg:m-0">
            <Text className="font-semibold">
              Post any IT questions, and get quick answers from verified IT
              experts at no cost!
            </Text>
            <Divider className="-mx-6 my-6" />
            {user ? (
              <CommentForm
                commentGroupId={documentData.commentGroup}
                documentType={documentData.docType}
                onPublish={() =>
                  handleDocumentCommentEventSend({
                    action:
                      DocumentCommentEvent.properties.publishComment.action,
                  })
                }
              />
            ) : (
              <div className="flex gap-2">
                <Avatar />
                <Button
                  className="flex-grow"
                  onClick={() => openSignUpModal()}
                  variant="outline"
                >
                  Join to ask a question
                </Button>
              </div>
            )}
            <CommentList
              authorId={documentData.author?.id}
              commentGroupId={documentData.commentGroup}
              onPublish={({ commentCount }) =>
                handleDocumentCommentEventSend({
                  action: DocumentCommentEvent.properties.publishComment.action,
                  commentCount,
                })
              }
              onMenuClick={({ menu, commentCount }) =>
                handleDocumentCommentEventSend({
                  action:
                    DocumentCommentEvent.properties[
                      menu === "edit" ? "menuEdit" : "menuDelete"
                    ].action,
                  commentCount,
                })
              }
              onDelete={({ confirmed, commentCount }) =>
                handleDocumentCommentEventSend({
                  action:
                    DocumentCommentEvent.properties[
                      confirmed ? "confirmDelete" : "confirmCancel"
                    ].action,
                  commentCount,
                })
              }
              onLike={({
                liked,
                commentCount,
                singleCommentReactionCount,
                userType,
              }) =>
                handleCommentInteractionsEventSend({
                  userType,
                  action: liked
                    ? CommentInteractionsEvent.properties.likeComment.action
                    : CommentInteractionsEvent.properties.unlikeComment.action,
                  commentCount,
                  singleCommentReactionCount,
                })
              }
              onUserClick={({
                userType,
                commentCount,
                singleCommentReactionCount,
              }) =>
                handleCommentInteractionsEventSend({
                  userType,
                  action:
                    userType === "expert"
                      ? CommentInteractionsEvent.properties.openExpertProfile
                          .action
                      : "",
                  commentCount,
                  singleCommentReactionCount,
                })
              }
            />
          </Paper>
        }
        signUpCallNode={
          !user && <SignUpBanner trackingLocation="Document Page" />
        }
      />
    </Container>
  );
}

export function ErrorBoundary() {
  return (
    <Container component="main">
      <ComponentError />
    </Container>
  );
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title = [
    data?.documentData.content.prompts[0]?.title,
    "GuideStack",
  ].join(" | ");

  return [
    {
      title,
    },
    {
      property: "og:title",
      content: title,
    },
    {
      name: "description",
      content: data?.documentData.summary,
    },
    {
      name: "og:description",
      content: data?.documentData.summary,
    },
  ];
};
